import { useState } from 'react';
import { Search, SlidersHorizontal, X, Star } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const KEY_OPTIONS = ['', 'C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
const SOURCE_OPTIONS = [
  { value: '', label: 'Any source' },
  { value: 'apple_music', label: 'Apple Music' },
  { value: 'spotify', label: 'Spotify' },
  { value: 'shazam', label: 'Shazam' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'ai_discovery', label: 'AI Find' },
  { value: 'other', label: 'Other' },
];

/**
 * Composable search + filter bar.
 *
 * Controlled — parent passes `filters` (current state) and `onChange` (setter).
 * filters shape: { q, bpm_min, bpm_max, key, source, scale }
 */
export default function CrateSearchBar({ filters, onChange, onExportCSV, placeholder = 'Search title, artist, album…' }) {
  const [popoverOpen, setPopoverOpen] = useState(false);

  const update = (patch) => onChange({ ...filters, ...patch });
  const clearAll = () =>
    onChange({ q: '', bpm_min: '', bpm_max: '', key: '', source: '', want: false });

  // Count active non-text filters for the badge (treat BPM range as 1 filter)
  const activeFilterCount =
    (filters.bpm_min || filters.bpm_max ? 1 : 0) +
    (filters.key ? 1 : 0) +
    (filters.source ? 1 : 0) +
    (filters.want ? 1 : 0);

  const hasAnyFilter = !!filters.q || activeFilterCount > 0;

  return (
    <div className="flex items-center gap-2 w-full">
      <div className="relative flex-1 min-w-0">
        <Search
          className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none"
          aria-hidden="true"
        />
        <Input
          value={filters.q || ''}
          onChange={(e) => update({ q: e.target.value })}
          placeholder={placeholder}
          className="pl-9 pr-9 rounded-xl"
          data-testid="crate-search-input"
          aria-label="Search collection"
        />
        {filters.q && (
          <button
            type="button"
            onClick={() => update({ q: '' })}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label="Clear search"
            data-testid="crate-search-clear"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="rounded-xl relative shrink-0"
            data-testid="crate-filter-button"
            aria-label="Open filters"
          >
            <SlidersHorizontal className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Filters</span>
            {activeFilterCount > 0 && (
              <Badge
                className="ml-1.5 h-5 min-w-5 px-1.5 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-[10px]"
                aria-label={`${activeFilterCount} active filters`}
              >
                {activeFilterCount}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-80 bg-card border-border"
          align="end"
          data-testid="crate-filter-popover"
        >
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs uppercase font-mono text-muted-foreground tracking-wider">
                  BPM Range
                </Label>
                {(filters.bpm_min || filters.bpm_max) && (
                  <button
                    type="button"
                    onClick={() => update({ bpm_min: '', bpm_max: '' })}
                    className="text-[10px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
                  >
                    clear
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  placeholder="Min"
                  value={filters.bpm_min || ''}
                  onChange={(e) => update({ bpm_min: e.target.value })}
                  min="40"
                  max="250"
                  className="rounded-lg"
                  data-testid="crate-filter-bpm-min"
                />
                <span className="text-muted-foreground text-sm">–</span>
                <Input
                  type="number"
                  placeholder="Max"
                  value={filters.bpm_max || ''}
                  onChange={(e) => update({ bpm_max: e.target.value })}
                  min="40"
                  max="250"
                  className="rounded-lg"
                  data-testid="crate-filter-bpm-max"
                />
              </div>
              <div className="flex flex-wrap gap-1 mt-2">
                {[
                  { label: 'Hip-Hop', min: 70, max: 100 },
                  { label: 'House', min: 120, max: 130 },
                  { label: 'Techno', min: 125, max: 140 },
                  { label: 'DnB', min: 165, max: 180 },
                ].map((preset) => (
                  <button
                    key={preset.label}
                    type="button"
                    onClick={() => update({ bpm_min: String(preset.min), bpm_max: String(preset.max) })}
                    className="text-[10px] px-2 py-0.5 rounded-full bg-secondary text-muted-foreground hover:text-foreground hover:bg-secondary/70 transition-colors"
                    data-testid={`crate-filter-bpm-preset-${preset.label.toLowerCase()}`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase font-mono text-muted-foreground tracking-wider mb-2 block">
                Key
              </Label>
              <Select
                value={filters.key || ''}
                onValueChange={(v) => update({ key: v === '__any__' ? '' : v })}
              >
                <SelectTrigger className="rounded-lg" data-testid="crate-filter-key">
                  <SelectValue placeholder="Any key" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any__">Any key</SelectItem>
                  {KEY_OPTIONS.filter((k) => k).map((k) => (
                    <SelectItem key={k} value={k}>
                      {k}
                    </SelectItem>
                  ))}
                  {KEY_OPTIONS.filter((k) => k).map((k) => (
                    <SelectItem key={`${k}m`} value={`${k}m`}>
                      {k}m (minor)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs uppercase font-mono text-muted-foreground tracking-wider mb-2 block">
                Source
              </Label>
              <Select
                value={filters.source || ''}
                onValueChange={(v) => update({ source: v === '__any__' ? '' : v })}
              >
                <SelectTrigger className="rounded-lg" data-testid="crate-filter-source">
                  <SelectValue placeholder="Any source" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any__">Any source</SelectItem>
                  {SOURCE_OPTIONS.filter((o) => o.value).map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Want list toggle — filter to tracks the user is still after.
                Independent of `status`, so it applies across active + owned. */}
            <div className="flex items-center justify-between gap-3 pt-1">
              <div className="flex items-center gap-2 min-w-0">
                <Star className={`w-4 h-4 ${filters.want ? 'text-[var(--brand-amber,#FFB24A)] fill-[var(--brand-amber,#FFB24A)]' : 'text-muted-foreground'}`} />
                <div className="min-w-0">
                  <Label
                    htmlFor="crate-filter-want-toggle"
                    className="text-xs uppercase font-mono text-muted-foreground tracking-wider cursor-pointer"
                  >
                    Want list only
                  </Label>
                  <p className="text-[10px] text-muted-foreground/70 leading-tight mt-0.5">
                    Show only tracks you starred to acquire.
                  </p>
                </div>
              </div>
              <Switch
                id="crate-filter-want-toggle"
                checked={!!filters.want}
                onCheckedChange={(v) => update({ want: v })}
                data-testid="crate-filter-want-toggle"
              />
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-border">
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAll}
                disabled={!hasAnyFilter}
                className="text-xs h-8"
                data-testid="crate-filter-clear-all"
              >
                Clear all
              </Button>
              <Button
                size="sm"
                onClick={() => setPopoverOpen(false)}
                className="text-xs h-8 rounded-lg"
                data-testid="crate-filter-apply"
              >
                Done
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {onExportCSV && (
        <Button
          variant="outline"
          className="rounded-xl shrink-0 hidden sm:inline-flex"
          onClick={onExportCSV}
          data-testid="crate-export-csv"
          aria-label="Export collection as text"
        >
          Export as text
        </Button>
      )}
    </div>
  );
}
